import * as BaseApi from "api/lib/data/Base";
import Organizations from "api/lib/data/Organizations";
import * as React from "react";
import { Popup, Image } from "semantic-ui-react";
import { getPhotoUrl } from "src/utils";
import { IRoles } from "src/reducers/session";
import { UsersStatus, UserStatus } from "../Session/withAuthorization";

export enum Event {
  Value = "value",
  Child_Added = "child_added",
}

export enum ResourceType {
  datasource = "datasource",
  dashboard = "dashboard",
  analysis = "analysis",
}

export interface ResourceTypeLabel {
  singular: string;
  plural: string;
}

export interface IResource {
  api: BaseApi.default;
  name: string;
  id: string;
  type: ResourceTypeLabel;
}

export interface IListener {
  ref: any;
  event: firebase.database.EventType;
  listener: any;
}

export interface IBaseProps {
  authUser: any;
  orgId?: string;
  orgConfig?: any;
  location?: any;
  history?: any;
  usersStatus?: UsersStatus;
}

export interface IBaseState {
  listeners: IListener[];
}

export class Base<
  TProps extends IBaseProps,
  TState extends IBaseState
> extends React.Component<TProps, TState> {
  protected static RESOURCE_LABEL = {
    analysis: {
      singular: "analysis",
      plural: "analyses",
    },
    datasource: {
      singular: "datasource",
      plural: "datasources",
    },
    dashboard: {
      singular: "dashboard",
      plural: "dashboards",
    },
  };

  protected static BASE_INITAL_STATE = {
    listeners: [],
  };
  constructor(props: any) {
    super(props);
  }

  public componentWillUnmount = () => {
    this.removeListeners();
  };

  public getListner(
    ref: any,
    event: firebase.database.EventType,
    callback: (snapshot: firebase.database.DataSnapshot) => void,
    errorCallback?: (error: Error) => void
  ) {
    const listener = ref.on(
      event,
      callback,
      errorCallback ? errorCallback : this.errorCallback
    );
    const thisListener: IListener = {
      ref,
      event,
      listener,
    };
    this.addToListeners(thisListener);
    return listener;
  }

  private errorCallback = (error: Error) => {
    console.error(error);
  };

  public getOnceListner(
    ref: firebase.database.Reference,
    event: firebase.database.EventType,
    callback: (snapshot: firebase.database.DataSnapshot) => void,
    errorCallback?: (error: Error) => void
  ) {
    return ref.once(
      event,
      callback,
      errorCallback ? errorCallback : this.errorCallback
    );
  }

  protected removeListeners = () => {
    const { listeners } = this.state;
    listeners.forEach((thisListener: IListener) => {
      thisListener.ref.off(thisListener.event, thisListener.listener);
    });
  };

  protected getList = (
    object: BaseApi.default,
    uid: string,
    callback: (items: any) => void,
    errorCallback?: (error: Error) => void
  ) => {
    this.getListner(
      object.getIdsForUser(uid),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const dataObj = snapshot.val();
        if (dataObj) {
          const items: any[] = [];
          const promises = [];
          Object.keys(dataObj).forEach((id) => {
            promises.push(
              this.getOnceListner(
                object.getConfig(id),
                Event.Value,
                (configSnapshot: firebase.database.DataSnapshot) => {
                  const config = configSnapshot.val();
                  if (config) {
                    items.push({
                      id,
                      ...config,
                      roles: dataObj[id],
                    });
                  } else {
                    if (errorCallback) {
                      errorCallback(Error("no configs found"));
                    }
                  }
                }
              )
            );
          });
          Promise.all(promises).then(
            (values) => {
              callback(items);
            },
            errorCallback ? errorCallback : this.errorCallback
          );
        } else {
          if (errorCallback) {
            errorCallback(Error("no userIds found"));
          }
        }
      },
      errorCallback ? errorCallback : this.errorCallback
    );
  };

  protected getMembers = (
    callback: (data: any[]) => void,
    errorCallback?: (error: Error) => void
  ) => {
    const { authUser, orgId } = this.props;
    const orgs = new Organizations();
    const newReq = orgs.requestListMemebers(orgId, authUser.uid);

    this.getListner(
      orgs.getMembers(authUser.uid, newReq.key),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const dataObj = snapshot.val();
        if (dataObj) {
          const data = dataObj.members.map((member) => {
            if (member) {
              return {
                id: member.uid,
                name: member.name,
                email: member.email,
                type: member.owner
                  ? "owner"
                  : member.admin
                  ? "admin"
                  : "member",
              };
            } else {
              return {};
            }
          });
          callback(data);
        } else {
          callback([]);
        }
      },
      errorCallback ? errorCallback : this.errorCallback
    );
  };

  protected getAuthRolesForResource = (
    resource: BaseApi.default,
    resourceId: string,
    callback: (roles: IRoles) => void
  ) => {
    const { authUser } = this.props;
    this.getListner(
      resource.getAuthRoles(authUser.uid, resourceId),
      Event.Value,
      (authRolesSnapshot: firebase.database.DataSnapshot) => {
        const roles: IRoles = authRolesSnapshot.val();
        callback(roles);
      }
    );
  };

  private addToListeners = (newListener: IListener) => {
    this.setState({ listeners: this.state.listeners.concat(newListener) });
  };

  public static getAvatar = (member: any, userStatus: UserStatus) => {
    return (
      <Popup
        position="bottom left"
        key={member.email}
        trigger={
          <Image
            src={getPhotoUrl(member)}
            inline={true}
            avatar={true}
            spaced="right"
            style={{
              borderRadius: "0",
              opacity: userStatus ? (userStatus.online ? 1 : 0.5) : 0.5,
            }}
          />
        }
      >
        <Popup.Header>{member.name}</Popup.Header>
        <Popup.Content>
          {member.email}
          <br />
          {userStatus ? (userStatus.online ? "online" : "offline") : "offline"}
        </Popup.Content>
      </Popup>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/Common/Base.tsx